<template>
  <div
    ref="editorContainer"
    class="editor_container"
    :class="{
      'border-dark': store.theme.isDark,
      'border-light': !store.theme.isDark,
    }"
  >
    <div ref="editorRef" class="editor"></div>
  </div>
</template>

<script setup lang="ts">
import * as ace from "ace-builds";
import { AceFactory, DARK_MODE_THEME, LIGHT_MODE_THEME } from "../language-server/ace-factory";
import AceLanguageServer from "../language-server/ace-lsp-adapter";
import type SpecificationElement from "../mvc/base/specification-element";
import { PreferenceStore } from "../services/preference-store";
import { ref, onMounted, watch, onUnmounted, onUpdated, inject } from "vue";
import RappidContext from "../rappid/rappid-context";

const rappidContext: RappidContext = inject<RappidContext>("rappidContext");

const editorRef = ref(null);
const editorContainer = ref(null);

const editor = ref<ace.Ace.Editor>();
const renderer = ref<ace.Ace.VirtualRenderer>();
const store = PreferenceStore.INSTANCE;

const compProps = withDefaults(
  defineProps<{
    model: SpecificationElement;
    specification?: string;
  }>(),
  { specification: undefined }
);

onMounted(() => {
  renderEditor();
  //Send the specification initially to the language server after first load
  AceLanguageServer.getInstance().connect(editor.value, compProps.model.getSpecificationType());

  PreferenceStore.INSTANCE.on("change:theme", (evt, value) => {
    editor.value.setTheme(value.isDark ? DARK_MODE_THEME : LIGHT_MODE_THEME);
  });

  // enable line wrap on resize
  new ResizeObserver(() => {
    editor.value?.resize(true);
  }).observe(editorContainer.value);
});

watch(
  () => compProps.specification,
  () => {
    updateText();
  }
);

watch(
  () => compProps.model,
  () => {
    AceLanguageServer.getInstance().connect(editor.value, compProps.model.getSpecificationType());
    updateText();
  }
);

onUnmounted(() => {
  if (editor.value) {
    editor.value.destroy();
  }
});

onUpdated(() => {
  updateText();
});

function setFocus(focus: boolean) {
  if (focus) {
    editor.value?.focus();
    editor.value?.getSelection().moveCursorFileEnd();
    rappidContext.commandManager.initBatchCommand();
  } else {
    editor.value?.getSelection().clearSelection();
    rappidContext.commandManager.storeBatchCommand();
  }
  editor.value?.setReadOnly(!focus || PreferenceStore.INSTANCE.isReadOnly);
}

function renderEditor() {
  editor.value = AceFactory.createEmbeddedEditor(editorRef.value as HTMLElement, {
    showGutter: true,
    darkMode: store.theme.isDark,
  });
  renderer.value = editor.value.renderer;
  updateText();
  editor.value.on("change", () => {
    if (editor.value?.isFocused()) {
      updateModel();
    }
  });
  editor.value.on("focus", () => {
    AceLanguageServer.getInstance().connect(editor.value, compProps.model.getSpecificationType());
    setFocus(true);
  });
  editor.value.on("blur", () => {
    AceLanguageServer.getInstance().disconnect(editor.value);
    setFocus(false);
  });
}

function updateText() {
  const editorText = editor.value.getSession().getValue();
  const modelText = compProps.model?.getSpecification() || "";
  if (editorText !== modelText) editor.value.getSession().setValue(modelText);
}

function updateModel() {
  compProps.model.setSpecification(editor.value?.getSession().getValue());
}
</script>

<style>
.editor_container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative !important;
  min-height: 100px;
}
.editor {
  flex-grow: 1;
}
.editor_container.bordered {
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.38);
}
.editor_container.bordered:hover {
  border-color: rgba(0, 0, 0, 0.38);
}
.v-theme--dark .editor_container.bordered {
  border-color: rgba(255, 255, 255, 0.38);
}
.v-theme--dark .editor_container.bordered:hover {
  border-color: rgba(255, 255, 255, 0.38);
}
.editor_container.bordered .ace_active-line,
.editor_container.bordered .ace_gutter-active-line {
  background: transparent !important;
}
</style>
